import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { appInsights } from "../../config/appInsights";
import BackButtonComponent from "../../NewComponents/BackButton/BackButton.component";
import ToggleTab from "../../NewComponents/ToggleTab/ToggleTab.component";
import InvestmentDetails from "./Tabs/InvestmentDetails";
import InvestmentTransactions from "./Tabs/InvestmentTransactions";
import useFunderInvestment from "../../custom-hooks/useFunderInvestment";
import useFunderDetails from "../../custom-hooks/useFunderDetails";
import { errorHandler } from "../../helpers/errorHandler";

/**
 * One investment, fetched by the id in the URL.
 *
 * It used to render whichever list row it was opened from, passed through
 * router state. The lists no longer carry interest or maturity figures, and
 * a refresh lost the state altogether, so it reads the investment itself.
 */
export default function EachFunderInvestment() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("investment-details");

  const { data: investment, isLoading, error } = useFunderInvestment(id);

  // Only for the certificate and statement email. The investment read does
  // not carry it, and the investor read is cheaper than the transactions one.
  const { data: investorData } = useFunderDetails({
    pageNumber: 1,
    size: 1,
    FunderId: investment?.funder_id,
    enabled: Boolean(investment?.funder_id),
  });
  const investor = investorData?.data?.[0];
  const customerEmail = investor?.email || investor?.biz_email || "";

  useEffect(() => {
    appInsights.trackPageView({
      name: "Investment Details  - (EachFunderInvestment.jsx)",
      isLoggedIn: true,
    });
  }, []);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !investment) {
    return (
      <Fragment>
        <h3 className="page-title d-flex align-items-center">
          <BackButtonComponent /> Investment details
        </h3>
        <div className="alert alert-danger col-md-8 mt-4" role="alert">
          <p className="mb-1">
            {error ? errorHandler(error) : "We couldn't find this investment."}
          </p>
          <small>
            A pending investment has no details until it is booked on core
            banking.
          </small>
        </div>
      </Fragment>
    );
  }

  const { funder_name, reference_number, currency, transactions } = investment;

  return (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="page-title d-flex align-items-center">
          <BackButtonComponent /> {funder_name}
          {reference_number ? ` (REF: ${reference_number})` : ""}
        </h3>
      </div>
      <div className="alignToggleTabItems mb-3">
        <ToggleTab
          text="Investment Details"
          id="investment-details"
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <ToggleTab
          text="Investment Transactions"
          id="investment-transactions"
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
      <div className="col-8">
        {activeTab === "investment-details" ? (
          <InvestmentDetails
            investment={investment}
            customerEmail={customerEmail}
          />
        ) : (
          <InvestmentTransactions
            currency={currency}
            id={investment.id}
            customerEmail={customerEmail}
            totalDeposits={investment.total_deposits}
            totalWithdrawals={investment.total_withdrawals}
            transactions={transactions ?? []}
          />
        )}
      </div>
    </Fragment>
  );
}
